import React, { useState, useEffect, useRef } from "react";
import { doFetchJson } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { mfmOriginalUrl, mfmThumbUrl } from "Projects/CC_cubi_clan_project/Utils/MfmService";
import "./SectionViewer.css";

const VOL_W = 200; // SVG viewBox width for volume curve
const VOL_H = 28;  // SVG viewBox height

function fmt(s) {
  const v = Math.max(0, s || 0);
  return `${String(Math.floor(v / 60)).padStart(2, "0")}:${String(Math.floor(v % 60)).padStart(2, "0")}`;
}

// --- Volume Curve ------------------------------------------------------------
function VolumeCurve({ puntos, duracion, onUpdate }) {
  const svgRef = useRef(null);
  const draggingRef = useRef(null);
  const [pts, setPts] = useState([]);

  useEffect(() => {
    const d = duracion || 10;
    if (puntos && puntos.length > 0) {
      setPts([...puntos].sort((a, b) => a.tiempo - b.tiempo));
    } else {
      setPts([{ tiempo: 0, valor: 1 }, { tiempo: d, valor: 1 }]);
    }
  }, [puntos, duracion]);

  const toX = (t) => (duracion > 0 ? (t / duracion) * VOL_W : 0);
  const toY = (v) => (1 - Math.max(0, Math.min(1, v))) * VOL_H;

  const fromEvent = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const rawX = ((e.clientX - rect.left) / rect.width) * VOL_W;
    const rawY = ((e.clientY - rect.top) / rect.height) * VOL_H;
    return {
      tiempo: Math.max(0, Math.min(duracion || 10, (rawX / VOL_W) * (duracion || 10))),
      valor: Math.max(0, Math.min(1, 1 - rawY / VOL_H)),
    };
  };

  const onPtMouseDown = (e, idx) => {
    e.preventDefault();
    e.stopPropagation();
    draggingRef.current = idx;
  };

  const onSvgMouseMove = (e) => {
    if (draggingRef.current === null) return;
    const { tiempo, valor } = fromEvent(e);
    setPts((prev) => {
      const next = [...prev];
      next[draggingRef.current] = { tiempo, valor };
      return next.sort((a, b) => a.tiempo - b.tiempo);
    });
  };

  const onSvgMouseUp = () => {
    if (draggingRef.current !== null) {
      draggingRef.current = null;
      onUpdate && onUpdate([...pts]);
    }
  };

  const onSvgDblClick = (e) => {
    e.stopPropagation();
    const { tiempo, valor } = fromEvent(e);
    const next = [...pts, { tiempo, valor }].sort((a, b) => a.tiempo - b.tiempo);
    setPts(next);
    onUpdate && onUpdate(next);
  };

  const onPtContextMenu = (e, idx) => {
    e.preventDefault();
    e.stopPropagation();
    if (pts.length > 2) {
      const next = pts.filter((_, i) => i !== idx);
      setPts(next);
      onUpdate && onUpdate(next);
    }
  };

  const polyPts = pts.map((p) => `${toX(p.tiempo)},${toY(p.valor)}`).join(" ");

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${VOL_W} ${VOL_H}`}
      preserveAspectRatio="none"
      className="exhi-sv2-vol"
      onMouseMove={onSvgMouseMove}
      onMouseUp={onSvgMouseUp}
      onMouseLeave={onSvgMouseUp}
      onDoubleClick={onSvgDblClick}
    >
      <rect x="0" y="0" width={VOL_W} height={VOL_H} className="exhi-sv2-vol-bg" />
      <polyline points={polyPts} className="exhi-sv2-vol-line" />
      {pts.map((p, i) => (
        <circle
          key={i}
          cx={toX(p.tiempo)}
          cy={toY(p.valor)}
          r={4}
          className="exhi-sv2-vol-pt"
          onMouseDown={(e) => onPtMouseDown(e, i)}
          onContextMenu={(e) => onPtContextMenu(e, i)}
        />
      ))}
    </svg>
  );
}

// --- Clip Block --------------------------------------------------------------
function ClipBlock({ medio, sectionDur, onDelete, onVolumen }) {
  const d = sectionDur || 60;
  const clipDur =
    medio.duracion != null ? medio.duracion : Math.max(4, d - (medio.offsetInicio || 0));
  const left = ((medio.offsetInicio || 0) / d) * 100;
  const width = Math.max((clipDur / d) * 100, 1.5);

  return (
    <div
      className="exhi-sv2-clip"
      style={{ left: `${left}%`, width: `${width}%` }}
      title={medio.tituloMfm || medio.mfmUuid}
    >
      {medio.thumbUrl ? (
        <img src={medio.thumbUrl} alt="" className="exhi-sv2-clip-img" />
      ) : (
        <div className="exhi-sv2-clip-nothumb" />
      )}
      <span className="exhi-sv2-clip-lbl">{medio.tituloMfm || "—"}</span>
      <button
        className="exhi-sv2-clip-del"
        onClick={(e) => { e.stopPropagation(); onDelete(medio.idSeccionPistaMedio); }}
        title="Eliminar clip"
      >×</button>
      <VolumeCurve
        puntos={medio.puntoVolumen}
        duracion={clipDur}
        onUpdate={(updatedPts) => onVolumen(medio.idSeccionPistaMedio, updatedPts)}
      />
    </div>
  );
}

// --- Track Lane --------------------------------------------------------------
function TrackLane({ pista, sectionDur, onToggle, onDrop, onDelete, onVolumen }) {
  const [over, setOver] = useState(false);

  const onDragOver = (e) => {
    if (e.dataTransfer.types.includes("application/exhi-media")) {
      e.preventDefault();
      setOver(true);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setOver(false);
    try {
      const payload = JSON.parse(e.dataTransfer.getData("application/exhi-media"));
      onDrop(pista.idSeccionPista, payload);
    } catch { /* ignore */ }
  };

  return (
    <div className={`exhi-sv2-track${pista.activa ? "" : " is-muted"}`}>
      <div className="exhi-sv2-track-hd">
        <button
          className={`exhi-sv2-track-mute${pista.activa ? "" : " is-muted"}`}
          onClick={() => onToggle(pista.idSeccionPista, !pista.activa)}
          title={pista.activa ? "Silenciar" : "Activar"}
        >
          {pista.tipo === "VIDEO" ? "▶" : "♪"}
        </button>
        <span className="exhi-sv2-track-nm" title={pista.nombre}>{pista.nombre}</span>
      </div>
      <div
        className={`exhi-sv2-lane${over ? " is-over" : ""}`}
        onDragOver={onDragOver}
        onDragLeave={() => setOver(false)}
        onDrop={handleDrop}
      >
        {(pista.medios || []).map((m) => (
          <ClipBlock
            key={m.idSeccionPistaMedio}
            medio={m}
            sectionDur={sectionDur}
            onDelete={onDelete}
            onVolumen={onVolumen}
          />
        ))}
      </div>
    </div>
  );
}

// --- Main SectionViewer ------------------------------------------------------
export default function SectionViewer({ section }) {
  const [pistas, setPistas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrent] = useState(0);
  const [isPlaying, setPlaying] = useState(false);

  const playingRef  = useRef(false);
  const audioCtxRef = useRef(null);
  const ctxStartRef = useRef(0);
  const secStartRef = useRef(0);
  const animRef     = useRef(null);
  const scheduled   = useRef([]);
  const mediaNodes  = useRef({});
  const videoEls    = useRef({});
  const audioEls    = useRef({});

  const dur = section?.duracion || 60;

  // Load pistas
  useEffect(() => {
    if (!section?.idSeccion) { setPistas([]); return; }
    setLoading(true);
    doFetchJson("EXHI", `secciones/${section.idSeccion}/pistas`, { method: "GET" })
      .then((res) => { if (res?.STATE === "OK") setPistas(res.DATA || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [section?.idSeccion]);

  // Reset on section change
  useEffect(() => {
    doStop();
    setCurrent(0);
    mediaNodes.current = {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section?.idSeccion]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancelAnimationFrame(animRef.current);
      if (audioCtxRef.current?.state !== "closed") audioCtxRef.current?.close();
    };
  }, []);

  function tick() {
    if (!playingRef.current || !audioCtxRef.current) return;
    const elapsed = audioCtxRef.current.currentTime - ctxStartRef.current;
    const t = Math.min(secStartRef.current + elapsed, dur);
    setCurrent(t);
    if (t < dur) {
      animRef.current = requestAnimationFrame(tick);
    } else {
      doStop();
      setCurrent(dur);
    }
  }

  function applyVolumeCurve(gainNode, ctx, puntos, clipDur, atTime) {
    const d = clipDur || 10;
    const sorted =
      puntos && puntos.length > 0
        ? [...puntos].sort((a, b) => a.tiempo - b.tiempo)
        : [{ tiempo: 0, valor: 1 }, { tiempo: d, valor: 1 }];
    gainNode.gain.cancelScheduledValues(atTime);
    gainNode.gain.setValueAtTime(sorted[0]?.valor ?? 1, atTime);
    for (let i = 1; i < sorted.length; i++) {
      gainNode.gain.linearRampToValueAtTime(sorted[i].valor, atTime + sorted[i].tiempo);
    }
  }

  async function doPlay() {
    if (playingRef.current) return;
    if (!audioCtxRef.current || audioCtxRef.current.state === "closed") {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      mediaNodes.current = {};
    }
    const ctx = audioCtxRef.current;
    if (ctx.state === "suspended") await ctx.resume();

    ctxStartRef.current = ctx.currentTime;
    secStartRef.current = currentTime;
    playingRef.current = true;
    setPlaying(true);

    for (const pista of pistas) {
      if (!pista.activa) continue;
      for (const medio of pista.medios || []) {
        const offset  = medio.offsetInicio || 0;
        const clipDur = medio.duracion || null;
        const trimIn  = medio.trimInicio || 0;
        const delay   = offset - currentTime;
        const skipIn  = delay < 0 ? -delay : 0;
        const when    = delay > 0 ? ctx.currentTime + delay : ctx.currentTime;

        const elMap = pista.tipo === "VIDEO" ? videoEls.current : audioEls.current;
        const el = elMap[medio.idSeccionPistaMedio];
        if (!el) continue;

        if (!mediaNodes.current[medio.idSeccionPistaMedio]) {
          const src  = ctx.createMediaElementSource(el);
          const gain = ctx.createGain();
          src.connect(gain);
          gain.connect(ctx.destination);
          mediaNodes.current[medio.idSeccionPistaMedio] = { gain };
        }
        const { gain } = mediaNodes.current[medio.idSeccionPistaMedio];

        applyVolumeCurve(gain, ctx, medio.puntoVolumen, clipDur || el.duration, when);
        el.currentTime = trimIn + skipIn;

        if (delay <= 0) {
          el.play().catch(() => {});
        } else {
          const tid = setTimeout(() => {
            if (playingRef.current) el.play().catch(() => {});
          }, delay * 1000);
          scheduled.current.push({ stop: () => clearTimeout(tid) });
        }
        scheduled.current.push({ stop: () => el.pause() });
      }
    }

    animRef.current = requestAnimationFrame(tick);
  }

  function doPause() {
    cancelAnimationFrame(animRef.current);
    scheduled.current.forEach((n) => { try { n.stop(); } catch { /* ignore */ } });
    scheduled.current = [];
    if (audioCtxRef.current?.state === "running") audioCtxRef.current.suspend();
    playingRef.current = false;
    setPlaying(false);
  }

  function doStop() {
    cancelAnimationFrame(animRef.current);
    scheduled.current.forEach((n) => { try { n.stop(); } catch { /* ignore */ } });
    scheduled.current = [];
    if (audioCtxRef.current?.state !== "closed") audioCtxRef.current?.suspend();
    playingRef.current = false;
    setPlaying(false);
    Object.values(videoEls.current).forEach((v) => v.pause());
    Object.values(audioEls.current).forEach((a) => a.pause());
  }

  const handleScrub = (e) => {
    const t = parseFloat(e.target.value);
    setCurrent(t);
    if (playingRef.current) doPause();
  };

  async function handleToggle(idPista, activa) {
    const res = await doFetchJson("EXHI", `pistas/${idPista}/activa`, {
      method: "PUT",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: `activa=${activa}`,
    });
    if (res?.STATE === "OK") {
      setPistas((prev) =>
        prev.map((p) => (p.idSeccionPista === idPista ? { ...p, activa } : p))
      );
    }
  }

  async function handleDrop(idPista, payload) {
    const { uuid, mediaType } = payload;
    const body = new URLSearchParams({
      mfmUuid: uuid,
      mediaType: mediaType || "AUDIO",
      offsetInicio: "0",
    });
    const res = await doFetchJson("EXHI", `pistas/${idPista}/medios`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });
    if (res?.STATE === "OK") {
      const r2 = await doFetchJson("EXHI", `secciones/${section.idSeccion}/pistas`, { method: "GET" });
      if (r2?.STATE === "OK") setPistas(r2.DATA || []);
    }
  }

  async function handleDeleteClip(idPistaMedio) {
    if (playingRef.current) doPause();
    const res = await doFetchJson("EXHI", `pistas/medios/${idPistaMedio}`, { method: "DELETE" });
    if (res?.STATE === "OK") {
      delete mediaNodes.current[idPistaMedio];
      setPistas((prev) =>
        prev.map((p) => ({
          ...p,
          medios: (p.medios || []).filter((m) => m.idSeccionPistaMedio !== idPistaMedio),
        }))
      );
    }
  }

  async function handleVolumen(idPistaMedio, updatedPts) {
    await doFetchJson("EXHI", `pistas/medios/${idPistaMedio}/volumen`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updatedPts.map((p) => ({ tiempo: p.tiempo, valor: p.valor }))),
    });
    setPistas((prev) =>
      prev.map((p) => ({
        ...p,
        medios: (p.medios || []).map((m) =>
          m.idSeccionPistaMedio === idPistaMedio ? { ...m, puntoVolumen: updatedPts } : m
        ),
      }))
    );
    if (playingRef.current && audioCtxRef.current && mediaNodes.current[idPistaMedio]) {
      const { gain } = mediaNodes.current[idPistaMedio];
      const pista = pistas.find((p) =>
        (p.medios || []).some((m) => m.idSeccionPistaMedio === idPistaMedio)
      );
      const medio = pista?.medios?.find((m) => m.idSeccionPistaMedio === idPistaMedio);
      applyVolumeCurve(gain, audioCtxRef.current, updatedPts, medio?.duracion || 10, audioCtxRef.current.currentTime);
    }
  }

  if (!section) {
    return (
      <div className="exhi-sv2 exhi-sv2--empty">
        <span className="text-muted small">Selecciona una sección para visualizarla</span>
      </div>
    );
  }

  const medioVisual = section.medioVisual;
  const tiempoFin   = section.tiempoFin ?? (section.tiempoInicio + dur);
  const sectionMedia = [
    ...(medioVisual ? [medioVisual] : []),
    ...(section.mediosAudio || []),
  ];

  const allVideoClips = pistas
    .filter((p) => p.tipo === "VIDEO")
    .flatMap((p) => (p.medios || []).map((m) => ({ ...m, _pistaActiva: p.activa })));

  const allAudioClips = pistas
    .filter((p) => p.tipo === "AUDIO")
    .flatMap((p) => (p.medios || []));

  const isClipActive = (m, t) => {
    const start = Number(m.offsetInicio) || 0;
    const end = start + (m.duracion != null ? Number(m.duracion) : dur - start);
    return t >= start && t < end;
  };

  return (
    <div className="exhi-sv2">
      <div className="exhi-sv2-header">
        <span className="exhi-sv2-title">{section.titulo}</span>
        <span className="exhi-sv2-meta">
          {fmt(section.tiempoInicio)} – {fmt(tiempoFin)} · {dur}s
        </span>
      </div>

      <div className="exhi-sv2-body">
        <div className="exhi-sv2-left">
          <div className="exhi-sv2-player">
            {allVideoClips.map((m) => {
              const active = m._pistaActiva && isClipActive(m, currentTime);
              if (m.mediaType === "IMAGE") {
                return (
                  <img
                    key={m.idSeccionPistaMedio}
                    src={mfmOriginalUrl(m.mfmUuid)}
                    alt=""
                    className={`exhi-sv2-player-vid${active ? " is-active" : ""}`}
                  />
                );
              }
              return (
                <video
                  key={m.idSeccionPistaMedio}
                  src={mfmOriginalUrl(m.mfmUuid)}
                  className={`exhi-sv2-player-vid${active ? " is-active" : ""}`}
                  ref={(el) => {
                    if (el) videoEls.current[m.idSeccionPistaMedio] = el;
                    else delete videoEls.current[m.idSeccionPistaMedio];
                  }}
                />
              );
            })}
            {allVideoClips.length === 0 && (
              medioVisual?.mediaType === "VIDEO" ? (
                <video
                  src={mfmOriginalUrl(medioVisual.mfmUuid)}
                  poster={medioVisual.thumbUrl || mfmThumbUrl(medioVisual.mfmUuid)}
                  className="exhi-sv2-player-vid is-active"
                />
              ) : medioVisual?.mfmUuid ? (
                <img
                  src={medioVisual.thumbUrl || mfmThumbUrl(medioVisual.mfmUuid)}
                  alt={section.titulo}
                  className="exhi-sv2-player-vid is-active"
                />
              ) : (
                <div className="exhi-sv2-no-media">Sin medio visual</div>
              )
            )}
            {allAudioClips.map((m) => (
              <audio
                key={m.idSeccionPistaMedio}
                src={mfmOriginalUrl(m.mfmUuid)}
                style={{ display: "none" }}
                ref={(el) => {
                  if (el) audioEls.current[m.idSeccionPistaMedio] = el;
                  else delete audioEls.current[m.idSeccionPistaMedio];
                }}
              />
            ))}
            <span className="exhi-sv2-time-overlay">{fmt(currentTime)}</span>
          </div>

          <div className="exhi-sv2-transport">
            <button className="exhi-sv2-tb" onClick={doStop} title="Parar">⏹</button>
            {isPlaying ? (
              <button className="exhi-sv2-tb exhi-sv2-tb--active" onClick={doPause} title="Pausa">⏸</button>
            ) : (
              <button className="exhi-sv2-tb" onClick={doPlay} title="Reproducir">▶</button>
            )}
            <input
              type="range"
              className="exhi-sv2-scrubber"
              min={0}
              max={dur}
              step={0.1}
              value={currentTime}
              onChange={handleScrub}
            />
            <span className="exhi-sv2-dur">{fmt(dur)}</span>
          </div>
          <div className="exhi-sv2-tracks">
            {loading && <div className="exhi-sv2-loading">Cargando pistas…</div>}
            {!loading && pistas.length === 0 && (
              <div className="exhi-sv2-no-tracks">
                <span>Sin pistas</span>
                <button
                  className="exhi-sv2-init-btn"
                  onClick={() => {
                    console.log("[SectionViewer] Creando pistas para sección", section.idSeccion);
                    setLoading(true);
                    doFetchJson("EXHI", `secciones/${section.idSeccion}/pistas/defecto`, { method: "POST" })
                      .then((res) => {
                        console.log("[SectionViewer] Respuesta pistas/defecto:", res);
                        if (res?.STATE === "OK") setPistas(res.DATA || []);
                      })
                      .catch((err) => console.error("[SectionViewer] Error pistas/defecto:", err))
                      .finally(() => setLoading(false));
                  }}
                >
                  + Crear pistas por defecto
                </button>
              </div>
            )}
            {!loading && pistas.map((pista) => (
              <TrackLane
                key={pista.idSeccionPista}
                pista={pista}
                sectionDur={dur}
                onToggle={handleToggle}
                onDrop={handleDrop}
                onDelete={handleDeleteClip}
                onVolumen={handleVolumen}
              />
            ))}
          </div>
        </div>

        <div className="exhi-sv2-right">
          <div className="exhi-sv2-right-hd">Medios</div>
          {sectionMedia.length === 0 && (
            <p className="exhi-sv2-right-empty">Sin medios asociados</p>
          )}
          {sectionMedia.map((m, i) => {
            const name = m.tituloMfm || `Media ${i + 1}`;
            const payload = JSON.stringify({ uuid: m.mfmUuid, mediaType: m.mediaType, titulo: name });
            return (
              <div
                key={m.mfmUuid || i}
                className="exhi-sv2-chip"
                draggable
                onDragStart={(e) => e.dataTransfer.setData("application/exhi-media", payload)}
                title={`${name} (${m.mediaType})`}
              >
                <img
                  src={m.thumbUrl || mfmThumbUrl(m.mfmUuid)}
                  alt=""
                  className="exhi-sv2-chip-img"
                />
                <div className="exhi-sv2-chip-info">
                  <span className="exhi-sv2-chip-nm">{name}</span>
                  <span className="exhi-sv2-chip-type">{m.mediaType}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}